import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Avatar,
  ListItemAvatar,
  ListItemText,
  List,
  ListItem,
  Alert,
  InputAdornment,
} from "@mui/material";
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import { useService } from "contexts/ServiceContextProvider";
import { useProjects } from 'contexts/ProjectsContextProvider';
import { useTranslation } from "react-i18next";
import '../../../css/components/Calendar/Events/CreateEventsModal.css';
import '../../../css/main.css';
import { useSnackbar } from 'contexts/SnackbarContextProvider';

import { DateTime } from 'luxon';
import { convertToUTC } from "components/Custom/TimezoneConvertion.jsx";
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import ScrollAwareTooltip from "components/Custom/ScrollAwareTooltip";


const CreateEventModal = ({ open, onClose, workspaceId, userTimezone }) => {
  const { t } = useTranslation();
  const { getProjectWorkers, createWorkspaceEvent } = useService();
  const { projects: myProjects } = useProjects();
  const { showSnackbar } = useSnackbar();
  const workspaceContext = useWorkspaces();
  const myWorkspaces = workspaceContext.workspaces;
  const workspaceWorkers = workspaceContext.workspaceWorkers
  const [workers, setWorkers] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [manualSelectedWorkers, setManualSelectedWorkers] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [allDayEvent, setAllDayEvent] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const [colorCode, setColorCode] = useState("#3788d8");
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [originalTimeStart, setOriginalTimeStart] = useState('');
  const [originalTimeEnd, setOriginalTimeEnd] = useState('');

  const getCurrentDateTime = () => DateTime.now().toISO({ includeOffset: false }).substring(0, 16);

  const [isFirstOpen, setIsFirstOpen] = useState(true);  // Flag to track first time open

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (open) {
      if (isFirstOpen) {
        setSelectedWorkers([]);
        setManualSelectedWorkers([]);
        setWorkers([]);
        setErrorMessage('');
        setFieldErrors({});

        setDateStart(getCurrentDateTime());
        setDateEnd('');
        setIsFirstOpen(false);
      }
    } else {
      setIsFirstOpen(true);
    }
  }, [open, workspaceId]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!open) {
      setTitle('');
      setDescription('');
      setDateStart('');
      setDateEnd('');
      setAllDayEvent(false);
      setVisibility(false);
      setColorCode("#3788d8");
      setSelectedWorkers([]);
      setManualSelectedWorkers([]);
      setWorkers([]);
      setSelectedProject('');
      setFieldErrors({});
      setErrorMessage('');
    }
  }, [open]);

  const handleProjectChange = async (event) => {
    const projectId = event.target.value;
    setSelectedProject(projectId);

    try {
      setWorkers([]);
      setSelectedWorkers([]);
      setManualSelectedWorkers([]);

      const projectWorkers = await getProjectWorkers(myWorkspaces[0].id, projectId);

      const updatedManualSelectedWorkers = manualSelectedWorkers.filter((workerId) => {
        const worker = projectWorkers.find((projWorker) => projWorker.id === workerId);
        if (worker) {
          setSelectedWorkers((prevSelectedWorkers) => [
            ...prevSelectedWorkers,
            workerId,
          ]);
          return false;
        }
        return true;
      });

      const filteredProjectWorkers = projectWorkers.filter(
        (worker) => !selectedWorkers.includes(worker.id) && !updatedManualSelectedWorkers.includes(worker.id)
      );

      setWorkers(filteredProjectWorkers);
      setManualSelectedWorkers(updatedManualSelectedWorkers);
      setSelectedWorkers((prevSelectedWorkers) => [
        ...prevSelectedWorkers,
        ...filteredProjectWorkers.map((worker) => worker.id),
      ]);
    } catch (error) {
      console.error(t("calendar.project_workers_err"), error);
    }
  };

  const handleWorkerToggle = (workerId) => {
    if (manualSelectedWorkers.includes(workerId)) {
      setManualSelectedWorkers((prevSelectedWorkers) =>
        prevSelectedWorkers.filter((id) => id !== workerId)
      );
    } else {
      setManualSelectedWorkers((prevSelectedWorkers) => [
        ...prevSelectedWorkers,
        workerId,
      ]);
    }
  };

  const handleRemoveCollaborator = (workerId) => {
    // Remove the worker from both selected workers and manually selected workers
    setSelectedWorkers((prevSelectedWorkers) =>
      prevSelectedWorkers.filter((id) => id !== workerId)
    );
    setManualSelectedWorkers((prevSelectedWorkers) =>
      prevSelectedWorkers.filter((id) => id !== workerId)
    );

    // Optionally reset the selected project if there are no collaborators left
    if (selectedWorkers.length === 0 && manualSelectedWorkers.length === 0) {
      setSelectedProject('');
    }
  };

  /* eslint-disable no-useless-escape */
  const handleCreateEvent = async () => {
    if (!title || !dateStart || !dateEnd || (selectedWorkers.length === 0 && manualSelectedWorkers.length === 0)) {
      showSnackbar(t("calendar.req_fields_err"), 'error');
      return;
    }

    // Convert dates to DateTime for comparison
    const startDateTime = DateTime.fromISO(dateStart);
    const endDateTime = DateTime.fromISO(dateEnd);

    // Check if the end date is earlier or equal to the start date
    if ((allDayEvent && endDateTime < startDateTime) || (!allDayEvent && endDateTime <= startDateTime)) {
      showSnackbar(t("calendar.end_date_invalid"), 'error');
      return;
    }

    let start, end;
    if (allDayEvent) {
      // Use only the date portion and set to UTC for all-day events
      start = startDateTime.toUTC().toISODate();
      end = endDateTime.plus({ days: 1 }).toUTC().toISODate(); // Ensure end date is exclusive
    } else {
      // Convert start and end times to UTC for non-all-day events
      start = convertToUTC(dateStart, userTimezone);
      end = convertToUTC(dateEnd, userTimezone);
    }

    const eventData = {
      title,
      description,
      date_start: start,
      date_end: end,
      all_day_event: allDayEvent,
      visibility,
      collaborators: [...selectedWorkers, ...manualSelectedWorkers],
      color_code: colorCode,
    };

    try {
      await createWorkspaceEvent(eventData, workspaceId);
      window.location.reload(true);
      onClose();
    } catch (error) {
      if (error.response && error.response.data) {
        const errors = error.response.data;
        const newFieldErrors = {};

        // Loop through all fields and check for errors
        Object.keys(errors).forEach((field) => {
          newFieldErrors[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
        });

        // Check if there's an 'error' field in the response and clean up the message
        let errorMessage = errors.error;

        // Manually clean the error message to remove surrounding brackets if necessary
        if (typeof errorMessage === 'string') {
          // Remove the surrounding square brackets and extra quotes if they exist
          errorMessage = errorMessage.replace(/^\[\'|\'\]$/g, '').replace(/\"/g, '');
        }

        // Now set the error message without the surrounding brackets or quotes
        setErrorMessage(errorMessage);
        setFieldErrors(newFieldErrors);
      } else {
        // Handle unexpected error responses
        setErrorMessage(t("calendar.unexpected_err"));
        console.error("Error creating event:", error);
      }
    }
  };

  const filteredCollaborators = [
    ...workers.filter((worker) => selectedWorkers.includes(worker.id)),
    ...workspaceWorkers.filter((worker) => manualSelectedWorkers.includes(worker.id)),
  ].filter(worker => worker.full_name.toLowerCase().includes(searchTerm.toLowerCase()));

  const availableWorkspaceWorkers = workspaceWorkers.filter(
    (worker) => !filteredCollaborators.some((selectedWorker) => selectedWorker.id === worker.id)
  ).filter(worker => worker.full_name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    if (allDayEvent) {
      // Store original times before switching to all-day
      if (dateStart) {
        const dtStart = DateTime.fromISO(dateStart);
        setOriginalTimeStart(dtStart.toFormat('HH:mm'));
        setDateStart(dtStart.toISODate()); // Just keep the date
      }
      if (dateEnd) {
        const dtEnd = DateTime.fromISO(dateEnd);
        setOriginalTimeEnd(dtEnd.toFormat('HH:mm'));
        setDateEnd(dtEnd.toISODate()); // Just keep the date
      }
    } else {
      // Restore original times when switching back
      if (originalTimeStart) {
        setDateStart(DateTime.fromISO(dateStart).toFormat(`yyyy-MM-dd'T'${originalTimeStart}`));
      }
      if (originalTimeEnd) {
        setDateEnd(DateTime.fromISO(dateEnd).toFormat(`yyyy-MM-dd'T'${originalTimeEnd}`));
      }
    }
  }, [allDayEvent]);

  const isYearValid = (date) => {
    const dateObj = DateTime.fromISO(date);

    return !isNaN(dateObj.year) && dateObj.year <= 9999 && dateObj.year >= 1000;
  };

  const startYearValidationError = !isYearValid(dateStart);
  const endYearValidationError = !isYearValid(dateEnd);

  const isSubmitDisabled = (DateTime.fromISO(dateStart) > DateTime.fromISO(dateEnd) && allDayEvent) ||
      (DateTime.fromISO(dateStart) >= DateTime.fromISO(dateEnd) && !allDayEvent);

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="create-event-main-style">
        <Typography variant="h5" gutterBottom className="create-event-title">
          {t("calendar.create_event")}
        </Typography>
        {errorMessage && (
          <Alert severity="error" className="create-event-error">
            {errorMessage}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              label={t('calendar.event_title') + "*"}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('calendar.event_title_placeholder')}
              error={!!fieldErrors.title}
              helperText={fieldErrors.title}
              className="create-event-title-text"
              InputLabelProps={{ shrink: true, className: "create-event-title" }}
              InputProps={{ className: "white-txt-color" }}
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              multiline
              label={t('calendar.event_description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="create-event-title-text"
              InputLabelProps={{ shrink: true, className: "create-event-title" }}
              InputProps={{ className: "white-txt-color" }}
            />
          </Grid>

          {/* Date and Time */}
          <Grid item xs={12} sm={6}>
            <ScrollAwareTooltip
              disableInteractive
              open={isSubmitDisabled || startYearValidationError} // Only show the tooltip when the button is disabled
            >
              <TextField
                type={allDayEvent ? "date" : "datetime-local"}
                label={t('global.start_date')  + "*"}
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                error={!!fieldErrors.date_start}
                helperText={fieldErrors.date_start}
                className="create-event-title-text"
                InputLabelProps={{ shrink: true, className: "create-event-title" }}
                InputProps={{ className: "white-txt-color" }}
                inputProps={{
                  max: "9999-12-31", // Limits the maximum date to 4-digit year
                }}
                onInput={(e) => {
                  // Remove any year longer than 4 digits
                  const inputDate = e.target.value;
                  const year = inputDate.split("-")[0];
                  if (year.length > 4) {
                    e.target.value = inputDate.slice(0, 4) + inputDate.slice(5);
                  }
                }}
              />
            </ScrollAwareTooltip>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ScrollAwareTooltip
              title={!endYearValidationError && t("calendar.end_date_after_start_date")}
              disableInteractive
              open={isSubmitDisabled || endYearValidationError} // Only show the tooltip when the button is disabled
            >
              <TextField
                type={allDayEvent ? "date" : "datetime-local"}
                label={t('calendar.event_end_date')  + "*"}
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                error={!!fieldErrors.date_end}
                helperText={fieldErrors.date_end}
                className="create-event-title-text"
                InputLabelProps={{ shrink: true, className: "create-event-title" }}
                InputProps={{ className: "white-txt-color" }}
                inputProps={{
                  max: "9999-12-31", // Limits the maximum date to 4-digit year
                }}
                onInput={(e) => {
                  // Remove any year longer than 4 digits
                  const inputDate = e.target.value;
                  const year = inputDate.split("-")[0];
                  if (year.length > 4) {
                    e.target.value = inputDate.slice(0, 4) + inputDate.slice(5);
                  }
                }}
              />
            </ScrollAwareTooltip>
          </Grid>

          {/* All Day and Visibility Checkboxes */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={allDayEvent} onChange={(e) => setAllDayEvent(e.target.checked)} />}
                  label={t('calendar.event_all_day')}
                  className="create-event-title"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={visibility} onChange={(e) => setVisibility(e.target.checked)} />}
                  label={t('calendar.event_public')}
                  className="create-event-title"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Project Dropdown */}

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel className="create-event-title">{t("calendar.event_select_project")}</InputLabel>
              <Select
                value={selectedProject}
                onChange={handleProjectChange}
                label={t('calendar.event_select_project')}
                className="create-event-title-text"
                inputProps={{ className: "white-txt-color" }}
              >
                {myProjects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>


          {/* Color Picker */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('calendar.event_color')}
              value={colorCode}
              onChange={(e) => setColorCode(e.target.value)}
              type="color"
              className="create-event-title-text"
            />
          </Grid>

          {/* Search Collaborators */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('calendar.event_collab_search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="create-event-title-text create-event-search-field"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="create-event-title" />
                  </InputAdornment>
                ),
                className: "white-txt-color"
              }}
              InputLabelProps={{ className: "create-event-title" }}
            />
          </Grid>

          {/* Collaborators */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" className="create-event-title">
              {t('calendar.event_collabs')  + "*"}
            </Typography>
            <Box className="create-event-collaborators-box">
              <List>
                {filteredCollaborators.map((worker) => (
                  <ListItem
                    key={worker.id}
                    className={`create-event-collab-list-default ${selectedWorkers.includes(worker.id) ? 'create-event-collab-list-selected' : ''}`}
                    button
                    onClick={() => handleRemoveCollaborator(worker.id)}
                  >
                    <ListItemAvatar>
                      <Avatar src={worker.image} />
                    </ListItemAvatar>
                    <ListItemText primary={worker.full_name} className={`create-event-title ${selectedWorkers.includes(worker.id) ? 'white-txt-color' : ''}`} />
                    <RemoveCircleIcon className="create-event-remove-collab-icon" />
                  </ListItem>
                ))}
              </List>
            </Box>
            {fieldErrors.collaborators && (
              <Typography variant="body2" color="error">
                {fieldErrors.collaborators}
              </Typography>
            )}
          </Grid>

          {/* Add more workers */}
          <Grid item xs={12} sm={6}>
            <Typography variant="h6" className="create-event-title">
              {t('calendar.event_add_collabs')}
            </Typography>
            <Box className="create-event-collaborators-box">
              <List>
                {availableWorkspaceWorkers.map((worker) => (
                  <ListItem key={worker.id} button onClick={() => handleWorkerToggle(worker.id)}>
                    <ListItemAvatar>
                      <Avatar src={worker.image} />
                    </ListItemAvatar>
                    <ListItemText primary={worker.full_name} className="create-event-title" />
                    <AddCircleIcon className="create-event-add-collab-icon" />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleCreateEvent} className="button-dark-bg" disabled={isSubmitDisabled || (startYearValidationError || endYearValidationError)}>
              {t('calendar.create_event')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default CreateEventModal;
